/**
 * BgmCard — `.bgm-card` from the prototype (cms-redesign-mockup.html
 * ~4712–4742 CSS, ~8404–8430 markup). Picks a background music track
 * for a session with an inline lock toggle, mini preview, and Copy
 * URL + Remove actions.
 *
 *   <BgmCard
 *     options={renderBGMOptions()}          // [{ label, value, session_music_url }]
 *     selectedValue={formData.session_bgm_id}
 *     audioUrl={formData.session_music_url}
 *     isLocked={!!formData.is_bgm_locked}
 *     onSelectionChange={…}
 *     onLockChange={…}
 *     onRemove={…}
 *   />
 *
 * Behavior:
 *   • Native <select> for the picker (lighter than Autocomplete and
 *     the list never gets long enough to need search — the BGM
 *     catalog is a small curated set)
 *   • Lock chip on the right of the head row toggles the
 *     is_bgm_locked flag. Padlock icon flips solid when locked.
 *   • Selected preview shows a teal play-mini that plays/pauses the
 *     hidden <audio> element, plus the track name and duration line.
 *   • Copy URL + Remove buttons at the bottom — same affordance as
 *     v1, just restyled.
 *
 * Empty state (selectedValue === '0' or no match): the head row
 * stays visible (pick a track), but the selected-preview + action
 * row collapse so the card doesn't show stale controls.
 */
import React, { useRef, useState } from 'react';
import { cn } from '../cn';

/* ─── Icons ────────────────────────────────────────────────────── */

const LockOpenSvg = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 12, height: 12 }}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </svg>
);

const LockClosedSvg = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 12, height: 12 }}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const PlayMiniSvg = (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="6 4 20 12 6 20 6 4" />
  </svg>
);

const PauseMiniSvg = (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
    <rect x="6" y="5" width="4" height="14" rx="1" />
    <rect x="14" y="5" width="4" height="14" rx="1" />
  </svg>
);

/* ─── Helpers ──────────────────────────────────────────────────── */

function findOption(options, value) {
  return (options || []).find((o) => String(o.value) === String(value)) || null;
}

// Try to peel a duration suffix off a v1 BGM label, e.g.
// "Soft Rain · 12:00" → { name: "Soft Rain", duration: "12:00" }.
// Falls back to the whole label as the name.
function splitLabel(label) {
  if (!label) return { name: '', duration: '' };
  const m = String(label).match(/^(.*?)\s*[·•|]\s*(\d{1,2}:\d{2})\s*$/);
  if (m) return { name: m[1].trim(), duration: m[2] };
  return { name: String(label).trim(), duration: '' };
}

/* ─── Main ─────────────────────────────────────────────────────── */

export default function BgmCard({
  options = [],
  selectedValue = '0',
  audioUrl = '',
  isLocked = false,
  isLoading = false,
  onSelectionChange,
  onLockChange,
  onRemove,
  onCopyUrl,
  showLockToggle = true,
  className,
}) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [copied, setCopied] = useState(false);

  const selected = findOption(options, selectedValue);
  const isEmpty = !selected || String(selected.value) === '0' || !audioUrl;
  const { name, duration } = splitLabel(selected?.label);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  };

  const handleCopy = async () => {
    if (!audioUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(audioUrl);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      if (onCopyUrl) onCopyUrl(audioUrl);
    } catch {
      // Ignore — user can still select + copy from a focused input
    }
  };

  return (
    <div
      className={cn(className)}
      style={{
        background: 'rgb(var(--color-cream))', /* cream */
        border: '1px solid rgb(var(--color-sand))', /* sand */
        borderRadius: 8,
        padding: 14,
        minWidth: 0,
      }}
    >
      {/* Head — picker + (optional) lock chip */}
      <div
        className="flex items-center"
        style={{ gap: 10, marginBottom: 10 }}
      >
        <select
          className="input"
          value={String(selectedValue ?? '0')}
          onChange={(e) => {
            const next = findOption(options, e.target.value);
            if (onSelectionChange) onSelectionChange(next);
          }}
          disabled={isLoading}
          style={{
            flex: 1,
            minWidth: 0,
            background: 'rgb(var(--color-surface))', /* surface */
            border: '1px solid rgb(var(--color-sand))',
            borderRadius: 6,
            padding: '6px 10px',
            fontSize: 13,
            color: 'rgb(var(--color-navy))',
          }}
        >
          {options.map((opt) => (
            <option key={`${opt.prop || 'bgm'}-${opt.value}`} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {showLockToggle && (
          <button
            type="button"
            onClick={() => onLockChange?.(!isLocked)}
            className="inline-flex items-center font-condensed font-bold uppercase cursor-pointer"
            aria-pressed={isLocked}
            title={
              isLocked
                ? 'Locked — admins must explicitly unlock before changing'
                : 'Unlocked — anyone can change this'
            }
            style={{
              gap: 5,
              padding: '6px 10px',
              fontSize: 10.5,
              letterSpacing: '0.08em',
              color: isLocked ? '#5C7E72' /* teal-d */ : '#9CA1A8',
              background: isLocked ? '#75A29333' /* teal-soft */ : 'transparent',
              border: '1px solid',
              borderColor: isLocked ? 'rgb(var(--color-teal-green))' : 'rgb(var(--color-sand))',
              borderRadius: 999,
              flexShrink: 0,
              transition: 'all 160ms ease',
            }}
          >
            {isLocked ? LockClosedSvg : LockOpenSvg}
            {isLocked ? 'Locked' : 'Unlocked'}
          </button>
        )}
      </div>

      {/* Selected preview + actions — only when a real track is
          attached. The empty state is just the picker above. */}
      {!isEmpty && (
        <>
          <div
            className="flex items-center"
            style={{
              gap: 12,
              padding: 10,
              background: 'rgb(var(--color-surface))', /* surface */
              borderRadius: 6,
              marginBottom: 8,
              minWidth: 0,
            }}
          >
            <button
              type="button"
              onClick={togglePlay}
              aria-label={isPlaying ? 'Pause' : 'Play'}
              className="grid place-items-center cursor-pointer"
              style={{
                width: 30,
                height: 30,
                background: 'rgb(var(--color-teal-green))',
                color: '#fff',
                border: 0,
                borderRadius: '50%',
                boxShadow: '0 2px 6px #75a29340',
                flexShrink: 0,
              }}
            >
              {isPlaying ? PauseMiniSvg : PlayMiniSvg}
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                className="text-navy"
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={name}
              >
                {name || 'Background music'}
              </div>
              <div
                className="font-mono text-text-muted"
                style={{ fontSize: 11, marginTop: 1 }}
              >
                {duration ? `${duration} · ` : ''}loops underneath the session
              </div>
            </div>
          </div>

          {/* Hidden audio element drives the play-mini button. */}
          <audio
            ref={audioRef}
            src={audioUrl}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            preload="none"
          />

          <div className="flex" style={{ gap: 6 }}>
            <button
              type="button"
              onClick={handleCopy}
              disabled={!audioUrl}
              className="font-condensed font-bold uppercase cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                flex: 1,
                height: 28,
                padding: '0 10px',
                border: '1px solid rgb(var(--color-sand))',
                borderRadius: 4,
                background: 'rgb(var(--color-surface))',
                color: copied ? '#5C7E72' : 'rgb(var(--color-navy))',
                fontSize: 11,
                letterSpacing: '0.06em',
                transition: 'color 160ms ease',
              }}
            >
              {copied ? 'Copied!' : 'Copy URL'}
            </button>
            {onRemove && (
              <button
                type="button"
                onClick={onRemove}
                className="font-condensed font-bold uppercase cursor-pointer"
                style={{
                  height: 28,
                  padding: '0 10px',
                  border: '1px solid transparent',
                  borderRadius: 4,
                  background: 'transparent',
                  color: '#C4715B', /* coral-d */
                  fontSize: 11,
                  letterSpacing: '0.06em',
                }}
              >
                Remove
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
